/**
 * ViewSignedDocumentModal - Modale Visualizzazione Documento Firmato
 * Il Diario di Bordo - SaaS
 * 
 * Modale per visualizzare il contenuto di un documento che l'utente ha firmato.
 * Supporta:
 * - Contenuto Markdown (renderizzato)
 * - PDF (link per apertura/download)
 * - URL esterno (link con apertura in nuova tab)
 * - Metadati della firma (data, IP, metodo, ecc.)
 * 
 * @version 1.0.0
 * @created Febbraio 2025
 */

import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { legalApi } from '../../services/legalApi'
import {
  X,
  FileText,
  ExternalLink,
  Download,
  Clock,
  Shield,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Eye,
  Globe,
  FileIcon,
  Info,
  MapPin,
  Monitor
} from 'lucide-react'

/**
 * Props:
 * @param {boolean} isOpen - Se la modale è aperta
 * @param {function} onClose - Callback per chiusura
 * @param {object} acknowledgment - Oggetto firma (da legalDocuments.signed o .history)
 * @param {boolean} isHistory - Se è un documento storico (invalidato)
 */
export default function ViewSignedDocumentModal({ 
  isOpen, 
  onClose, 
  acknowledgment,
  isHistory = false 
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [documentData, setDocumentData] = useState(null)
  const [signatureData, setSignatureData] = useState(null)
  const [showDetails, setShowDetails] = useState(false)

  // Carica contenuto documento quando si apre la modale
  useEffect(() => {
    if (isOpen && acknowledgment?._id) {
      loadDocumentContent()
    }
    
    // Reset quando si chiude
    if (!isOpen) {
      setDocumentData(null)
      setSignatureData(null)
      setError(null)
      setShowDetails(false)
    }
  }, [isOpen, acknowledgment?._id])

  const loadDocumentContent = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await legalApi.getSignedDocumentContent(acknowledgment._id)
      
      if (response.data.success) {
        setDocumentData(response.data.data.document)
        setSignatureData(response.data.data.signature)
      } else {
        setError(response.data.message || 'Errore nel caricamento')
      }
    } catch (err) {
      console.error('Load signed document error:', err)
      setError(err.response?.data?.message || 'Impossibile caricare il documento')
    } finally {
      setLoading(false)
    }
  }

  // Non renderizzare se non aperta
  if (!isOpen) return null

  // Determina il tipo di contenuto
  const getContentType = () => {
    if (!documentData) return null
    if (documentData._documentDeleted) return 'deleted'
    if (documentData.contentSource === 'pdf' && documentData.pdf?.fileId) return 'pdf'
    if (documentData.contentSource === 'external_url' && documentData.content?.externalUrl) return 'external'
    if (documentData.content?.body) return 'markdown'
    return 'empty'
  }

  const contentType = getContentType()

  // Formatta data
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/D'
    return new Date(dateString).toLocaleString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // URL base per API
  const apiBaseUrl = import.meta.env.VITE_API_URL || ''

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 rounded-2xl shadow-xl transform transition-all">
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                isHistory 
                  ? 'bg-gray-100 dark:bg-gray-700' 
                  : 'bg-green-100 dark:bg-green-800/50'
              }`}>
                {isHistory ? (
                  <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {documentData?.title || acknowledgment?.documentName || 'Documento'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {documentData?.typeDisplayName || acknowledgment?.documentType} 
                  {' • '}
                  v{documentData?.version || acknowledgment?.documentVersion}
                  {isHistory && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                      Storico
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {/* Avviso versione diversa */}
            {documentData?._versionMismatch && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Versione aggiornata
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Hai firmato la versione {documentData._signedVersion}, ma il documento è stato aggiornato alla versione {documentData._currentVersion}. 
                      Stai visualizzando la versione corrente.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Caricamento documento...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12">
                <AlertTriangle className="h-12 w-12 text-amber-500 mb-3" />
                <p className="text-gray-700 dark:text-gray-300 font-medium">{error}</p>
                <button
                  onClick={loadDocumentContent}
                  className="mt-4 px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                >
                  Riprova
                </button>
              </div>
            ) : contentType === 'deleted' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  Documento non più disponibile
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md">
                  Il contenuto originale di questo documento è stato rimosso dal sistema.
                  I dettagli della tua firma sono comunque conservati.
                </p>
              </div>
            ) : contentType === 'pdf' ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl mb-4">
                  <FileIcon className="h-16 w-16 text-red-500" />
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
                  {documentData.pdf?.filename || 'Documento PDF'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {documentData.pdf?.size 
                    ? `${(documentData.pdf.size / 1024 / 1024).toFixed(2)} MB`
                    : 'File PDF'
                  }
                </p>
                <div className="flex gap-3">
                  <a
                    href={`${apiBaseUrl}/api/legal/pdf/${documentData.pdf.fileId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    Visualizza PDF
                  </a>
                  <a
                    href={`${apiBaseUrl}/api/legal/pdf/${documentData.pdf.fileId}`}
                    download={documentData.pdf?.filename}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Scarica
                  </a>
                </div>
              </div>
            ) : contentType === 'external' ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl mb-4">
                  <Globe className="h-16 w-16 text-blue-500" />
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
                  Documento ospitato esternamente
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md text-center">
                  Questo documento è disponibile su una piattaforma esterna
                </p>
                <a
                  href={documentData.content.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Apri documento
                </a>
                {documentData.content?.summary && (
                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg max-w-md">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Sommario:</strong> {documentData.content.summary}
                    </p>
                  </div>
                )}
              </div>
            ) : contentType === 'markdown' ? (
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>
                  {documentData.content.body}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  Nessun contenuto disponibile per questo documento
                </p>
              </div>
            )}
          </div>

          {/* Signature Details (collapsible) */}
          {signatureData && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Dettagli firma
                  </span>
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showDetails && (
                <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Data firma */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Data firma</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDateTime(signatureData.acknowledgedAt)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Metodo */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Shield className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Metodo</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {signatureData.methodDisplay}
                      </p>
                    </div>
                  </div>
                  
                  {/* Contesto */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Contesto</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {signatureData.contextDisplay}
                      </p>
                    </div>
                  </div>
                  
                  {/* IP Address */}
                  {signatureData.ipAddress && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Indirizzo IP</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                          {signatureData.ipAddress}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* User Agent (se presente) */}
                  {signatureData.userAgent && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg sm:col-span-2">
                      <Monitor className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Dispositivo</p>
                        <p className="text-sm text-gray-900 dark:text-white truncate" title={signatureData.userAgent}>
                          {signatureData.userAgent.length > 80 
                            ? signatureData.userAgent.substring(0, 80) + '...' 
                            : signatureData.userAgent
                          }
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Invalidazione (se storico) */}
                  {signatureData.invalidated && (
                    <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg sm:col-span-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-amber-600 dark:text-amber-400">Firma sostituita</p>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                          {signatureData.invalidatedAt 
                            ? `Il ${formatDateTime(signatureData.invalidatedAt)}`
                            : 'Data non disponibile'
                          }
                          {signatureData.invalidatedReason && (
                            <span className="block text-xs mt-1 font-normal">
                              Motivo: {
                                {
                                  'admin_request_resignature': 'Rifirma richiesta dall\'amministratore',
                                  'new_version_published': 'Pubblicata nuova versione',
                                  'document_updated': 'Documento aggiornato',
                                  'manual_revoke': 'Revoca manuale'
                                }[signatureData.invalidatedReason] || signatureData.invalidatedReason
                              }
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
